<script setup lang="ts">
import { computed, watch } from "vue";
import { fr } from 'date-fns/locale';
import { differenceInBusinessDays, format } from 'date-fns';
import { claimLegalStepById } from "@/utils/config/constants";

import { ClaimEventDetail } from '@/interfaces/data-contracts';
import BadgeLegalStep from '@/components/ui/badges/BadgeLegalStep.vue';
import { ArrowRightIcon } from "@heroicons/vue/24/solid";
import adminAPI from '@/services/api';
import { useClaimStore, useNotificationStore } from '@/store';
import { XMarkIcon } from "@heroicons/vue/20/solid";

interface Props {
  event: ClaimEventDetail,
  previousEvent?: ClaimEventDetail,
  index: number;
  timelineLength: number;
}
const props = defineProps<Props>();

const isLast = computed(() => {
  return (props.index + 1) === props.timelineLength
});

const eventDate = computed(() => format(new Date(props.event.eventDate), 'dd MMMM yyyy', { locale: fr }));

const legalStepName = (event: ClaimEventDetail) => claimLegalStepById(event.eventType.claimLegalStepId);
const eventsDiffInDays = computed(() => {
  const { event, previousEvent } = props;
  if (previousEvent)
    return differenceInBusinessDays(new Date(event.eventDate), new Date(previousEvent.eventDate))
});

async function deleteLastClaimEvent() {
  const claimId = useClaimStore().claim.id;
  const { success } = await adminAPI.removeLastClaimEvent(claimId);
  if (success) {
    useNotificationStore().emitNotification({
      title: "Dernier évènement supprimé"
    });
    useClaimStore().retrieveClaim(claimId)
  }
}
</script>
<template>
  <li :class="isLast && index ? 'pb-10' : 'py-4'" class="flex flex-wrap justify-between ml-6">
    <span class="absolute flex items-center justify-center w-6 h-6 bg-blue-100 rounded-full -left-3 ring-8 ring-white">
      <img class="rounded-full shadow-lg" v-if="event.admin?.profilePictureUrl" :src="event.admin.profilePictureUrl" />
      <span v-else class="inline-block w-6 h-6 overflow-hidden bg-gray-100 rounded-full">
        <svg class="w-full h-full text-gray-300" fill="currentColor" viewBox="0 0 24 24">
          <path d="M24 20.993V24H0v-2.996A14.977 14.977 0 0112.004 15c4.904 0 9.26 2.354 11.996 5.993zM16.002 8.999a4 4 0 11-8 0 4 4 0 018 0z" />
        </svg>
      </span>
      </span>
    <div>
      <!-- <div class="absolute w-3 h-3 bg-gray-200 rounded-full mt-1.5 -left-1.5 border border-white "></div> -->
      <div class="flex">
        <BadgeLegalStep v-if="previousEvent && previousEvent?.eventType.claimLegalStepId !== event.eventType.claimLegalStepId" :name="legalStepName(previousEvent)" rounded="rounded-md" class="" /> 
        <ArrowRightIcon v-if="previousEvent && previousEvent?.eventType.claimLegalStepId !== event.eventType.claimLegalStepId" class="w-5 h-5 mx-2 text-gray-300" />
        <BadgeLegalStep :name="legalStepName(event)" rounded="rounded-md" class="mr-4" />
      </div>
      <div class="flex py-2">
        <h3 class="text-base font-semibold text-gray-900 ">{{ event.eventType.displayName }}</h3>
        <time v-tooltip.right="eventsDiffInDays ? `${eventsDiffInDays} jours ouvrés après l'évènement précédent` : null"
          class="pt-1.5 px-2 shrink-0 text-sm font-normal leading-none text-gray-500">{{ eventDate }}</time>
        <span v-permission="['UPCLAIM_ADMIN']" v-if="index === 0 && !isLast" @click="deleteLastClaimEvent" class="pt-1 text-sm leading-6 cursor-pointer">
          <XMarkIcon class="w-5 h-5 text-gray-400" />
        </span>
      </div>
      <!-- @TODO: Tooltip to display how many days from previous event  -->
      <!-- Example : "15 jours après l'évènement précédent" -->
      <blockquote v-if="event.internalNote" class="mt-2 mb-3 text-base font-medium prose whitespace-pre-line gray-700">
        « {{ event.internalNote }} »
      </blockquote>
      <p v-if="event.eventType.description" class="mb-2 text-base font-normal prose text-gray-500 whitespace-pre-line">{{ event.eventType.description }}</p>
      <p v-if="event.admin?.fullName" class="text-gray-700">Ajouté par : {{ event.admin.fullName }}</p>
    </div>
    <div v-if="event.messageLink" v-permission="['LAWYER', 'UPCLAIM_ADMIN']" class="flex-shrink-0 block">
      <a :href="event.messageLink" class="relative inline-flex items-center px-4 py-2 my-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
        Voir le message
      </a>
    </div>
  </li>
</template>